import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import AnimatedCounter from "../components/AnimatedCounter";
import TestimonialsMarquee from "../components/TestimonialsMarquee";
import CourseCard from "../components/CourseCard";
import BuyNowModal from "../components/BuyNowModal";
import JsonLd from "../components/JsonLd";
import {
  Monitor,
  Smartphone,
  Sparkles,
  Zap,
  BookOpen,
  Trophy,
  IndianRupee,
  Users,
  ChevronRight,
  Rocket,
  ShieldCheck,
  PlayCircle,
} from "lucide-react";

export default function HomePage() {
  const { settings } = useSettings();
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  const handleBuyNow = (course) => {
    setSelectedCourse(course);
    setModalOpen(true);
  };

  const featured =
    courses.filter((c) => c.is_featured).length > 0
      ? courses.filter((c) => c.is_featured).slice(0, 6)
      : courses.slice(0, 6);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: "Aducation",
    alternateName: "Aducation - India's Most Affordable Learning Platform",
    url: typeof window !== "undefined" ? window.location.origin : "",
    logo: (typeof window !== "undefined" ? window.location.origin : "") + "/aducation-logo.png",
    description:
      "India's Most Affordable Learning Platform. Govt Exam preparation (SSC, Railway, Banking) and industry-ready Skills courses starting at just ₹99.",
    foundingDate: "2022",
    sameAs: [
      settings.youtube_url,
      settings.instagram_url,
      `https://t.me/${settings.telegram_username}`,
    ].filter(Boolean),
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: settings.support_email,
      availableLanguage: ["en", "hi"],
    },
  };

  const itemListSchema = featured.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: featured.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: (typeof window !== "undefined" ? window.location.origin : "") + `/courses/${c.slug}`,
      name: c.title,
    })),
  } : null;

  return (
    <div data-testid="home-page">
      <JsonLd data={orgSchema} id="jsonld-org" />
      {itemListSchema && <JsonLd data={itemListSchema} id="jsonld-featured-list" />}
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-24 lg:pt-24 lg:pb-32 relative">
          <div className="max-w-3xl">
            <div className="fade-up inline-flex items-center gap-2 gold-pill px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6">
              <Sparkles size={14} /> India's #1 Affordable Ed-Tech
            </div>
            <h1 className="fade-up fade-up-delay-1 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              <span className="text-white">India's Most </span>
              <span className="red-text">Affordable</span>
              <br />
              <span className="gold-text">Learning</span>
              <span className="text-white"> Platform.</span>
            </h1>
            <p className="fade-up fade-up-delay-2 mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Access India's most-loved teachers for <span className="text-amber-400 font-semibold">Govt Exams (SSC, Railway, Banking)</span> and industry-ready <span className="text-red-400 font-semibold">Skills courses</span> — starting at just <span className="gold-text font-bold">₹99</span>.
            </p>
            <div id="download" className="fade-up fade-up-delay-3 mt-9 flex flex-col sm:flex-row gap-3">
              <a
                data-testid="hero-download-windows"
                href={settings.windows_download_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white font-bold btn-red-glow hover:brightness-110 transition"
              >
                <Monitor size={22} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-widest opacity-80">Download for</div>
                  <div className="text-base leading-none">Windows PC</div>
                </div>
              </a>
              <a
                data-testid="hero-download-android"
                href={settings.android_download_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-bold btn-gold-glow hover:brightness-110 transition"
              >
                <Smartphone size={22} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-widest opacity-80">Download for</div>
                  <div className="text-base leading-none">Android APK</div>
                </div>
              </a>
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl border border-white/15 text-white font-semibold hover:bg-white/5 transition"
              >
                <PlayCircle size={20} /> Explore Courses
              </Link>
            </div>

            <div className="fade-up fade-up-delay-3 mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2"><ShieldCheck className="text-emerald-400" size={16} /> Verified Placements</div>
              <div className="flex items-center gap-2"><IndianRupee className="text-amber-400" size={16} /> Starts at ₹99</div>
              <div className="flex items-center gap-2"><Users className="text-red-400" size={16} /> 17K+ Students</div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-y border-red-500/30 bg-gradient-to-r from-red-950/40 via-red-900/20 to-red-950/40 overflow-hidden">
          <div className="ticker-track whitespace-nowrap py-2.5 text-sm font-semibold text-amber-300">
            🎯 Reasoning Special Batch @ ₹99 &nbsp; • &nbsp; 🚀 Full Stack MERN Course Live &nbsp; • &nbsp; 📚 SSC CGL 2025 Foundation Open &nbsp; • &nbsp; 🎓 100+ Students Placed at Google, Microsoft, Infosys &nbsp; • &nbsp; ✅ Register Once — Learn on Both PC & Android &nbsp;
          </div>
        </div>
      </section>

      {/* COUNTERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold mb-3">Success Milestones</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl">
            Trusted by <span className="red-text">Millions</span> across India
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <AnimatedCounter value={settings.stat_students} label="Total Students" accent="red" testId="counter-students" />
          <AnimatedCounter value={settings.stat_youtube_views} label="YouTube Views" accent="gold" testId="counter-youtube" />
          <AnimatedCounter value={settings.stat_watch_time} label="Total Watch-Time" accent="green" testId="counter-watchtime" />
        </div>
      </section>

      {/* FEATURED COURSES CAROUSEL */}
      <section className="py-16 lg:py-24 bg-[#0A0C0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold mb-3">Featured Courses</div>
              <h2 className="font-display font-bold text-3xl lg:text-4xl">
                Learn from <span className="gold-text">India's Best</span> Educators
              </h2>
            </div>
            <Link to="/courses" data-testid="home-view-all-courses" className="inline-flex items-center gap-1 text-amber-400 font-semibold hover:text-amber-300 group">
              View All Courses <ChevronRight size={16} className="group-hover:translate-x-1 transition" />
            </Link>
          </div>

          <div className="flex gap-5 overflow-x-auto hide-scrollbar pb-4 -mx-4 px-4 snap-x snap-mandatory">
            {featured.map((c) => (
              <div key={c.id} className="min-w-[280px] sm:min-w-[320px] max-w-[340px] snap-start">
                <CourseCard course={c} onBuyNow={handleBuyNow} />
              </div>
            ))}
            {featured.length === 0 && (
              <div className="text-slate-500 text-sm py-10">No courses available yet. Check back soon!</div>
            )}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold mb-3">Why Aducation?</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl">
            Two Tracks. <span className="red-text">One Destination.</span> <span className="gold-text">Your Success.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              icon: Trophy,
              tag: "Govt Job Track",
              title: "SSC, Railway, Banking, State Exams",
              desc: "Structured batches by top mentors like Balram Sir & Aman Vashishth Sir. Full syllabus coverage, 1000+ mock tests, live doubt sessions.",
              color: "red",
            },
            {
              icon: Rocket,
              tag: "Skills Track",
              title: "Coding, Data Science, Creator Economy",
              desc: "Job-ready bootcamps in Full Stack MERN, Data Science, Content Creation. Real projects + placement support + GitHub portfolio.",
              color: "gold",
            },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="glass rounded-3xl p-8 lg:p-10 relative overflow-hidden group hover:border-amber-400/40 transition">
                <div className={`absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl ${f.color === "red" ? "bg-red-500/15" : "bg-amber-500/15"}`} />
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${f.color === "red" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"} mb-5`}>
                  <Icon size={26} />
                </div>
                <div className={`text-[11px] uppercase tracking-widest font-bold mb-2 ${f.color === "red" ? "text-red-400" : "text-amber-400"}`}>
                  {f.tag}
                </div>
                <h3 className="font-display font-bold text-2xl mb-3 text-white">{f.title}</h3>
                <p className="text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FEATURES / BENEFITS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24">
        <div className="grid md:grid-cols-4 gap-5">
          {[
            { icon: BookOpen, label: "Live + Recorded Lectures", value: "300+ hrs" },
            { icon: Users, label: "Doubt Sessions Weekly", value: "24×7" },
            { icon: Zap, label: "Full PDF Notes & Tests", value: "1000+" },
            { icon: IndianRupee, label: "Starting Price", value: "Just ₹99" },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="glass rounded-2xl p-6 text-center hover:border-amber-400/40 transition">
                <Icon size={26} className="mx-auto text-amber-400 mb-3" />
                <div className="font-display font-bold text-white text-xl">{f.value}</div>
                <div className="text-xs text-slate-400 mt-1">{f.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 lg:py-24 bg-[#0A0C0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold mb-3">Real Stories, Real Success</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl">
              Our Students at <span className="red-text">Google</span>, <span className="text-white">Microsoft</span>, <span className="gold-text">SSC</span>, <span className="text-white">Railway</span> & More
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
              From Income Tax Officers to Software Engineers — every story starts at Aducation.
            </p>
          </div>
        </div>
        <TestimonialsMarquee testimonials={testimonials} />
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="relative rounded-3xl overflow-hidden glass p-10 lg:p-16 text-center">
          <div className="hero-glow absolute inset-0" />
          <div className="relative">
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight">
              Ready to <span className="red-text">Crack</span> Your Dream? <br className="hidden lg:block" />
              <span className="gold-text">Start Today</span> for just ₹99.
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">
              Download the Aducation app. Register once. Learn on both Windows PC & Android.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                data-testid="cta-download-windows"
                href={settings.windows_download_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white font-bold btn-red-glow"
              >
                <Monitor size={20} /> Download Windows
              </a>
              <a
                data-testid="cta-download-android"
                href={settings.android_download_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-bold btn-gold-glow"
              >
                <Smartphone size={20} /> Download Android
              </a>
            </div>
          </div>
        </div>
      </section>

      <BuyNowModal open={modalOpen} onClose={() => setModalOpen(false)} course={selectedCourse} />
    </div>
  );
}
